/* ============================================================================
   ew-gamified-cart.js — IceBeanie gamified sliding cart (sticky cart drawer)
   Behaviour ported from IceBeanie_Gamified_Cart.html, wired to Shopify:
     - open / close (overlay, ESC, focus return, scroll lock, focus trap)
     - add / change / remove via /cart/*.js + Section Rendering API
       (server re-renders sections/ew-gamified-cart.liquid; JS swaps [data-ewc-body])
     - reward ladder: progress fill animates from previous value, node "pop" +
       confetti burst when a reward is newly unlocked
     - reservation countdown persisted in sessionStorage (survives navigation)
     - header cart-count sync ([data-ewc-count])
     - Dawn contract (<cart-drawer>: getSectionsToRender / setActiveElement /
       renderContents) so product-form.js quick-add renders here too
     - global fallback: any non-Dawn <form action="/cart/add"> is AJAX'd into the drawer
   Public API: window.EWCart = { add, change, remove, refresh, open, close }
   Namespaced on [data-ewc-*]; never double-binds with ew-cart.js / ew-pdp-v2.js.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'ew-gamified-cart';
  var STORE_KEY = 'ewc:resv-start';
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var lastFocus = null;
  var timerId = null;
  var busy = false;

  /* ------------------------------------------------------------ helpers */
  function root() { return document.querySelector('cart-drawer[data-ewc]'); }
  function body() { var r = root(); return r ? r.querySelector('[data-ewc-body]') : null; }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function routeBase() {
    var b = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return b.charAt(b.length - 1) === '/' ? b : b + '/';
  }
  function storeGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function storeSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }
  function storeDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) {} }

  function snapshot(b) {
    if (!b) return { count: 0, fill: 0, unlocked: [], empty: true };
    return {
      count: parseInt(b.getAttribute('data-count') || '0', 10),
      fill: parseFloat(b.getAttribute('data-fill') || '0'),
      unlocked: (b.getAttribute('data-unlocked') || '').split(',').filter(Boolean),
      empty: b.getAttribute('data-empty') === 'true'
    };
  }

  /* ------------------------------------------------------------ open / close */
  function isOpen() { var r = root(); return !!(r && r.classList.contains('is-open')); }

  function open(trigger) {
    var r = root(); if (!r) return;
    if (!isOpen()) lastFocus = (trigger && trigger.focus) ? trigger : document.activeElement;
    r.classList.add('is-open');
    r.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ewc-lock');
    ensureTimer();
    var c = r.querySelector('[data-ewc-close-btn]');
    if (c) window.setTimeout(function () { try { c.focus({ preventScroll: true }); } catch (e) { c.focus(); } }, 60);
  }

  function close() {
    var r = root(); if (!r || !isOpen()) return;
    r.classList.remove('is-open');
    r.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ewc-lock');
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) { lastFocus.focus(); }
    }
    lastFocus = null;
  }

  function trapTab(e) {
    var r = root(); if (!r || !isOpen() || e.key !== 'Tab') return;
    var panel = r.querySelector('.ewc-panel');
    var f = qa('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------------------ busy / errors */
  function setBusy(on) {
    busy = on;
    var r = root(); if (!r) return;
    r.classList.toggle('is-busy', on);
    r.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  function showError(msg) {
    var b = body(); if (!b) return;
    var el = b.querySelector('[data-ewc-error]'); if (!el) return;
    el.textContent = msg || (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    el.hidden = false;
    window.clearTimeout(el._t);
    el._t = window.setTimeout(function () { el.hidden = true; }, 4500);
  }

  /* ------------------------------------------------------------ API */
  function request(url, payload) {
    payload.sections = SECTION_ID;
    payload.sections_url = window.location.pathname;
    return window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.text().then(function (txt) {
        var json; try { json = JSON.parse(txt); } catch (e) { json = { description: 'Unexpected response from the cart. Please try again.' }; }
        return { ok: res.ok, status: res.status, json: json };
      });
    });
  }

  function handle(result, openAfter) {
    if (!result.ok) {
      var j = result.json || {};
      showError(j.description || j.message || null);
      throw new Error(j.description || j.message || 'cart request failed');
    }
    var html = result.json && result.json.sections && result.json.sections[SECTION_ID];
    if (html) render(html); else refresh();
    if (openAfter) open();
    return result.json;
  }

  function add(items, opts) {
    opts = opts || {};
    var list = Array.isArray(items) ? items : [items];
    list = list.map(function (i) { return { id: +i.id, quantity: +(i.quantity || 1), properties: i.properties, selling_plan: i.selling_plan }; });
    setBusy(true);
    return request(routeBase() + 'cart/add.js', { items: list })
      .then(function (r) { return handle(r, opts.open !== false); })
      .finally(function () { setBusy(false); });
  }

  function change(key, quantity) {
    setBusy(true);
    return request(routeBase() + 'cart/change.js', { id: String(key), quantity: Math.max(0, +quantity) })
      .then(function (r) { return handle(r, false); })
      .finally(function () { setBusy(false); });
  }

  function remove(key) { return change(key, 0); }

  /* ------------------------------------------------------------ timed promo (discount code) */
  var CLEARED_KEY = 'ewc:promo-cleared';
  function promoConfigured() { var b = body(); return !!(b && b.getAttribute('data-promo-enabled') === 'true'); }
  function promoSecs() { var b = body(); return parseInt((b && b.getAttribute('data-resv-secs')) || '0', 10); }
  function promoRemaining() {
    if (!promoConfigured()) return 0;
    var start = parseInt(storeGet(STORE_KEY) || '0', 10);
    if (!start) return promoSecs();            // window not started yet -> full
    return Math.max(0, promoSecs() - Math.floor((Date.now() - start) / 1000));
  }
  function promoActive() { return promoConfigured() && promoRemaining() > 0; }

  // Toggle the promo/regular price rows on every card; clear the codes once when the window ends.
  function applyPromoState() {
    var r = root(); if (!r) return;
    if (!promoConfigured()) { r.classList.remove('ewc-promo-on', 'ewc-promo-off'); return; }
    var on = promoActive();
    r.classList.toggle('ewc-promo-on', on);
    r.classList.toggle('ewc-promo-off', !on);
    if (!on) ensurePromoCleared();
  }

  // At 0:00: strip the discount code(s) from the cart so every promo line reverts to full price.
  function ensurePromoCleared() {
    if (storeGet(CLEARED_KEY) === '1') return;      // already handled this window
    storeSet(CLEARED_KEY, '1');
    setBusy(true);
    request(routeBase() + 'cart/update.js', { discount: '' })
      .then(function (res) {
        var html = res.json && res.json.sections && res.json.sections[SECTION_ID];
        if (html) render(html); else refresh();
      })
      .catch(function () {})
      .finally(function () { setBusy(false); });
  }

  // Add an upsell, then apply its discount code while the window is open; verify the real line price.
  // Every promo code needed by the products CURRENTLY in the cart, plus `extra`.
  // /cart/update.js REPLACES the cart's discount codes, so a single code would wipe the ones
  // applied earlier. Codes must be sent together, comma-separated and with no spaces.
  function promoCodesForCart(extra) {
    var b = body(), out = [];
    function push(c) { if (c && out.indexOf(c) === -1) out.push(c); }
    if (b) {
      qa('[data-ewc-up][data-has-promo="1"]', b).forEach(function (card) {
        var pid = card.getAttribute('data-pid');
        if (pid && b.querySelector('[data-ewc-line][data-pid="' + pid + '"]')) push(card.getAttribute('data-ewc-promo-code'));
      });
    }
    push(extra);
    return out;
  }

  function addUpsell(variantId, code) {
    setBusy(true);
    return request(routeBase() + 'cart/add.js', { items: [{ id: +variantId, quantity: 1 }] })
      .then(function (r) {
        if (!r.ok) { var j = r.json || {}; showError(j.description || j.message || null); throw new Error('add failed'); }
        // Render first so the new line is in the DOM and promoCodesForCart() can see it.
        var html = r.json && r.json.sections && r.json.sections[SECTION_ID];
        if (html) render(html);
        if (!promoActive()) return null;
        var codes = promoCodesForCart(code);
        if (!codes.length) return null;
        return request(routeBase() + 'cart/update.js', { discount: codes.join(',') });
      })
      .then(function (r) {
        if (r) {
          var html = r.json && r.json.sections && r.json.sections[SECTION_ID];
          if (html) render(html); else refresh();
        }
        verifyPromoLines();
      })
      .finally(function () { setBusy(false); });
  }

  // The card advertises the promo price; the line item is the truth. If a promo product is in the
  // cart but its real line price is above the promo price, the code didn't apply (usage limit reached
  // / expired) -> flip that card to regular price + show the "offer ended" note.
  function verifyPromoLines() {
    var b = body(); if (!b) return;
    qa('[data-ewc-up][data-has-promo="1"]', b).forEach(function (card) {
      var pid = card.getAttribute('data-pid');
      var promoEach = parseInt(card.getAttribute('data-promo-price') || '0', 10);
      var line = b.querySelector('[data-ewc-line][data-pid="' + pid + '"]');
      var ended = false;
      if (line && promoEach > 0) {
        var qty = parseInt(line.getAttribute('data-qty') || '1', 10);
        var finalP = parseInt(line.getAttribute('data-final') || '0', 10);
        ended = (finalP - promoEach * qty) > 3;   // charged more than promo (3c rounding tolerance)
      }
      card.classList.toggle('ewc-ended', ended);
      var note = card.querySelector('[data-ewc-ended-note]'); if (note) note.hidden = !ended;
    });
  }

  function refresh() {
    var u;
    try { u = new URL(window.location.href); u.searchParams.set('sections', SECTION_ID); u = u.toString(); }
    catch (e) { u = window.location.pathname + '?sections=' + SECTION_ID; }
    return window.fetch(u, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) { if (j && j[SECTION_ID]) render(j[SECTION_ID]); })
      .catch(function () {});
  }

  /* ------------------------------------------------------------ render */
  function render(html) {
    var r = root(), b = body(); if (!r || !b) return;
    var doc = new window.DOMParser().parseFromString(html, 'text/html');
    var nb = doc.querySelector('[data-ewc-body]'); if (!nb) return;

    var before = snapshot(b);
    b.innerHTML = nb.innerHTML;
    ['data-count', 'data-empty', 'data-fill', 'data-unlocked', 'data-subtotal', 'data-resv-secs', 'data-resv-hot', 'data-resv-loop', 'data-promo-enabled']
      .forEach(function (a) { var v = nb.getAttribute(a); if (v !== null) b.setAttribute(a, v); });
    var after = snapshot(b);

    r.classList.toggle('is-empty', after.empty);
    afterRender(before, after);
  }

  function afterRender(before, after) {
    var b = body(); if (!b) return;

    // progress fill: animate from previous value
    var fill = b.querySelector('[data-ewc-fill]');
    if (fill && !reduce) {
      fill.style.transition = 'none';
      fill.style.width = before.fill + '%';
      void fill.offsetWidth;
      fill.style.transition = '';
      fill.style.width = after.fill + '%';
    }

    // newly unlocked rewards -> node pop + confetti
    var fresh = after.unlocked.filter(function (k) { return before.unlocked.indexOf(k) === -1; });
    fresh.forEach(function (k) {
      var n = b.querySelector('[data-ewc-node="' + k + '"]');
      if (n) { n.classList.remove('pdone'); void n.offsetWidth; n.classList.add('pdone'); }
    });
    if (fresh.length) burst();

    updateCounts(after.count);
    ensureTimer();
    applyPromoState();
    verifyPromoLines();
  }

  function updateCounts(n) {
    qa('[data-ewc-count]').forEach(function (el) {
      el.textContent = n;
      var mode = el.getAttribute('data-ewc-count');
      el.hidden = (mode === 'hide-zero' && !(n > 0));
    });
    // Dawn bubble (if the stock header is ever re-enabled)
    var dawn = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    if (dawn) dawn.textContent = n;
  }

  /* ------------------------------------------------------------ confetti */
  function burst() {
    if (reduce) return;
    var b = body(); var fx = b && b.querySelector('[data-ewc-fx]'); if (!fx || !fx.animate) return;
    var marks = ['\u2744', '\u2744', '\u2022', '\u2744'];
    for (var i = 0; i < 18; i++) {
      var s = document.createElement('span'); s.className = 'ewc-flake';
      s.textContent = marks[i % marks.length];
      s.style.left = (15 + Math.random() * 70) + '%'; s.style.top = '20px';
      s.style.color = Math.random() > .5 ? '#128FE0' : '#4FC7F2';
      s.style.fontSize = (9 + Math.random() * 10) + 'px';
      fx.appendChild(s);
      var dx = (Math.random() * 2 - 1) * 120, dy = 60 + Math.random() * 90, rot = (Math.random() * 2 - 1) * 220;
      s.animate(
        [{ transform: 'translate(0,0) rotate(0)', opacity: 1 },
         { transform: 'translate(' + dx + 'px,' + dy + 'px) rotate(' + rot + 'deg)', opacity: 0 }],
        { duration: 900 + Math.random() * 500, easing: 'cubic-bezier(.2,.6,.4,1)' }
      ).onfinish = function () { this.effect.target.remove(); };
    }
  }

  /* ------------------------------------------------------------ reservation timer */
  function timerCfg() {
    var b = body();
    return {
      secs: parseInt((b && b.getAttribute('data-resv-secs')) || '600', 10),
      hot: parseInt((b && b.getAttribute('data-resv-hot')) || '120', 10),
      loop: !!(b && b.getAttribute('data-resv-loop') === 'true')
    };
  }
  function ensureTimer() {
    var b = body(); if (!b) return;
    if (b.getAttribute('data-empty') === 'true') { storeDel(STORE_KEY); storeDel(CLEARED_KEY); stopTimer(); return; }
    if (!storeGet(STORE_KEY)) { storeSet(STORE_KEY, String(Date.now())); storeDel(CLEARED_KEY); } // fresh window
    // Run the interval when there is a visible timer OR a promo to expire (headless countdown).
    if (!b.querySelector('[data-ewc-resv-time]') && !promoConfigured()) { stopTimer(); return; }
    if (!timerId) timerId = window.setInterval(tick, 1000);
    tick();
  }
  function stopTimer() { if (timerId) { window.clearInterval(timerId); timerId = null; } }
  function tick() {
    var b = body(); if (!b) { stopTimer(); return; }
    var el = b.querySelector('[data-ewc-resv-time]'), wrap = b.querySelector('[data-ewc-resv]');
    if (!el && !promoConfigured()) { stopTimer(); return; }
    var cfg = timerCfg();
    var start = parseInt(storeGet(STORE_KEY) || '0', 10) || Date.now();
    var left = cfg.secs - Math.floor((Date.now() - start) / 1000);
    if (left <= 0) {
      if (cfg.loop) { storeSet(STORE_KEY, String(Date.now())); storeDel(CLEARED_KEY); left = cfg.secs; }
      else left = 0;
    }
    if (el && wrap) {
      var m = Math.floor(left / 60), s = left % 60;
      el.textContent = m + ':' + (s < 10 ? '0' : '') + s;
      wrap.classList.toggle('hot', left <= cfg.hot);
    }
    applyPromoState();
  }

  /* ------------------------------------------------------------ custom element (Dawn contract) */
  class EWCartDrawer extends HTMLElement {
    connectedCallback() { bindDrawer(this); }
    // Dawn product-form.js contract
    open(t) { open(t); }
    close() { close(); }
    getSectionsToRender() { return [{ id: SECTION_ID, selector: '[data-ewc-body]' }]; }
    setActiveElement(el) { this.activeElement = el; }
    renderContents(response) {
      var html = response && response.sections && response.sections[SECTION_ID];
      if (html) render(html); else refresh();
      open(this.activeElement);
    }
  }

  function bindDrawer(self) {
    if (!self || self._ewcBound) return;
    self._ewcBound = true;

    // delegated clicks inside the drawer
    self.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : null; if (!t) return;
      var el;

      if ((el = t.closest('[data-ewc-close]'))) { e.preventDefault(); close(); return; }

      if (busy) return;

      if ((el = t.closest('[data-ewc-qty]'))) {
        e.preventDefault();
        var wrap = el.closest('.ewc-qty');
        var val = wrap ? parseInt((wrap.querySelector('[data-ewc-qty-val]') || {}).textContent || '1', 10) : 1;
        var next = val + parseInt(el.getAttribute('data-delta') || '0', 10);
        if (next < 1) return; // proposal: main line never drops below 1 via the stepper (use Remove)
        change(el.getAttribute('data-key'), next).catch(function () {});
        return;
      }

      if ((el = t.closest('[data-ewc-remove]'))) {
        e.preventDefault();
        remove(el.getAttribute('data-key')).catch(function () {});
        return;
      }

      if ((el = t.closest('[data-ewc-upsell]'))) {
        e.preventDefault();
        if (el.getAttribute('data-added') === '1') {
          remove(el.getAttribute('data-line-key')).catch(function () {});
        } else {
          addUpsell(el.getAttribute('data-variant-id'), el.getAttribute('data-ewc-promo-code')).catch(function () {});
        }
        return;
      }
    });

    // checkout: real navigation (POST /cart with name=checkout); label parity with the proposal
    self.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || !f.hasAttribute || !f.hasAttribute('data-ewc-checkout-form')) return;
      var lbl = f.querySelector('[data-ewc-checkout-label]');
      if (lbl) lbl.textContent = 'Redirecting\u2026';
    });

    // initial state
    var b = body();
    if (b) {
      self.classList.toggle('is-empty', b.getAttribute('data-empty') === 'true');
      updateCounts(parseInt(b.getAttribute('data-count') || '0', 10));
      if (b.getAttribute('data-empty') === 'true') storeDel(STORE_KEY);
      if (self.getAttribute('data-open-on-load') === 'true' && b.getAttribute('data-empty') !== 'true') {
        window.setTimeout(function () { open(); }, 450);
      }
      // Start the countdown + promo state even if the drawer is never opened, so the promo can expire.
      if (b.getAttribute('data-empty') !== 'true') ensureTimer();
      applyPromoState();
      verifyPromoLines();
    }
  }

  if (window.customElements && !window.customElements.get('cart-drawer')) {
    window.customElements.define('cart-drawer', EWCartDrawer);
  }

  /* ------------------------------------------------------------ document-level wiring */
  function onDocClick(e) {
    var t = e.target; if (!t || !t.closest) return;
    var opener = t.closest('[data-ewc-open]');
    if (!opener) {
      // legacy header links that were not annotated
      opener = t.closest('.ice-header-user-icon a[href$="/cart"], .c2-icon[href$="/cart"], #cart-icon-bubble');
    }
    if (opener && root()) { e.preventDefault(); open(opener); }
  }

  function onDocKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) { if (isOpen()) close(); return; }
    trapTab(e);
  }

  // Any plain <form action="/cart/add"> (Webflow-era sections) not handled by Dawn's product-form
  function onDocSubmit(e) {
    var f = e.target;
    if (!f || !f.matches || !f.matches('form[action*="/cart/add"]')) return;
    if (f.closest('product-form') || f.hasAttribute('data-ewc-native') || !root()) return;
    var fd = new window.FormData(f);
    var id = fd.get('id'); if (!id) return;
    e.preventDefault();
    var item = { id: +id, quantity: +(fd.get('quantity') || 1) };
    var props = {};
    fd.forEach(function (v, k) { var m = /^properties\[(.+)\]$/.exec(k); if (m) props[m[1]] = v; });
    if (Object.keys(props).length) item.properties = props;
    if (fd.get('selling_plan')) item.selling_plan = +fd.get('selling_plan');
    add([item]).catch(function () {});
  }

  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKey);
  document.addEventListener('submit', onDocSubmit, true);

  // theme editor: show the drawer while the section is selected
  document.addEventListener('shopify:section:select', function (e) {
    if (e.detail && e.detail.sectionId === SECTION_ID) open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.detail && e.detail.sectionId === SECTION_ID) close();
  });

  // Dawn pub/sub (cart page quantity changes etc.) -> silent refresh
  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
    try {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (d) {
        if (d && d.source === 'product-form') return; // handled through renderContents()
        refresh();
      });
    } catch (err) {}
  }

  /* ------------------------------------------------------------ public API */
  window.EWCart = { add: add, change: change, remove: remove, refresh: refresh, open: open, close: close, isOpen: isOpen, addUpsell: addUpsell, promoActive: promoActive, promoRemaining: promoRemaining, promoCodes: promoCodesForCart };
})();